'use client'

import Cookies from 'js-cookie'
import { useCallback, useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'

import type { Tenant } from '@/services/http/tenantService/types'
import { AUTH_COOKIE, TENANT_COOKIE } from '@/utils/constants'

// Singleton instance map
const socketInstances = new Map<string, Socket>()

export const useWebSocket = (servicePath: string) => {
  const [socket, setSocket] = useState<Socket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>()

  const getTenant = useCallback(() => {
    return JSON.parse(Cookies.get(TENANT_COOKIE) || 'null') as Tenant | null
  }, [])

  const getToken = useCallback(() => {
    return (Cookies.get(AUTH_COOKIE) || 'null') as string | null
  }, [])

  const connect = useCallback(() => {
    const tenant = getTenant()
    const token = getToken()
    if (!tenant || !token) return

    // Check if we already have a socket instance for this service
    let socketInstance = socketInstances.get(servicePath)

    if (!socketInstance) {
      socketInstance = io(process.env.NEXT_PUBLIC_API_WSS_URL, {
        path: servicePath,
        transports: ['websocket'],
        auth: {
          tennant: tenant.id,
          userToken: token,
        },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      })

      socketInstances.set(servicePath, socketInstance)
    }

    setSocket(socketInstance)

    const onConnect = () => {
      console.info(
        `Connected WebSocket to ${servicePath}, tenant: ${tenant.id}`,
      )
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = undefined
      }
    }

    const onDisconnect = (reason: string) => {
      console.info(
        `Disconnected WebSocket from ${servicePath}, tenant: ${tenant.id}, reason: ${reason}`,
      )

      if (reason !== 'io client disconnect') {
        reconnectTimeoutRef.current = setTimeout(() => {
          connect()
        }, 1000)
      }
    }

    const onError = (error: Error) => {
      console.error(
        `WebSocket error for ${servicePath}, tenant: ${tenant.id}`,
        error,
      )
    }

    socketInstance.on('connect', onConnect)
    socketInstance.on('disconnect', onDisconnect)
    socketInstance.on('error', onError)

    return () => {
      socketInstance?.off('connect', onConnect)
      socketInstance?.off('disconnect', onDisconnect)
      socketInstance?.off('error', onError)
    }
  }, [servicePath, getTenant, getToken])

  useEffect(() => {
    const cleanup = connect()
    return () => {
      cleanup?.()
    }
  }, [connect])

  // Cleanup function to be called when the component unmounts
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [])

  return { socket }
}
